import * as React from "react";
import { GetSnapshotEventGroupedResponseItem } from "@/modules/snapshots/businessReports.types";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useSnapshotEventsContext } from "@/modules/snapshots/snapshotEvents/useSnapshotEventsContext";
import { Tooltip } from "@/components/ui/tooltip";

interface TimelineChartProps {
  chartWidth: number;
  chartHeight: number;
  leftMargin: number;
  timelineWidth: number;
  data: Record<string, GetSnapshotEventGroupedResponseItem[]>;
  monthMarkers: Array<number>;
  getDatePosition: (dateStr: string) => number;
  getStatusColor: (status: string, is_overdue: boolean) => string;
  handleMouseEnter: (
    event: GetSnapshotEventGroupedResponseItem,
    mouseEvent: React.MouseEvent<SVGGElement, MouseEvent>,
  ) => void;
  handleMouseLeave: () => void;
  hoveredEvent: GetSnapshotEventGroupedResponseItem | null;
}

export const TimelineChart: React.FC<TimelineChartProps> = ({
  chartWidth,
  chartHeight,
  leftMargin,
  timelineWidth,
  data,
  monthMarkers,
  getDatePosition,
  getStatusColor,
  handleMouseEnter,
  handleMouseLeave,
}) => {
  const { formatMonthYear } = useDateFormatter();
  const { setSelectedSnapshotEventGroupIds } = useSnapshotEventsContext();

  if (Object.keys(data).length === 0) {
    return (
      <div className="rounded-lg border border-[#e9e0d5] bg-white p-5 text-center text-muted-foreground">
        No data available
      </div>
    );
  }
  const maxMonthMarkers = 24;
  const montParameter = Math.floor(monthMarkers.length / maxMonthMarkers) + 1;
  const reducedMonthMarkers = monthMarkers.filter((_, index) => index % montParameter === 0);
  const todayMarker = getDatePosition(new Date().toISOString());

  return (
    <div className="overflow-auto" style={{ maxHeight: "calc(-250px + 64vh)" }}>
      <svg width={chartWidth} height={chartHeight + 100} className="block w-full max-w-full">
        {/* Timeline Header */}
        <g>
          {reducedMonthMarkers.map((marker, index) => {
            const position = getDatePosition(new Date(marker).toISOString());

            return (
              <g key={index}>
                {/* Month Labels */}
                <text
                  x={position}
                  y={35}
                  textAnchor="middle"
                  className="fill-current text-xs font-medium text-muted-foreground"
                >
                  {formatMonthYear(new Date(marker).toISOString())}
                </text>
                <line
                  x1={position}
                  y1={45}
                  x2={position}
                  y2={chartHeight + 45}
                  stroke="#dde0e4"
                  strokeWidth="1"
                  strokeDasharray="2,2"
                />
              </g>
            );
          })}

          <g>
            <line
              x1={todayMarker}
              y1={45}
              x2={todayMarker}
              y2={chartHeight + 45}
              stroke="#3b82f6"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
          </g>
        </g>

        {/*Main Timeline Line */}
        <line x1={leftMargin} y1={55} x2={leftMargin + timelineWidth} y2={55} stroke="#0a213e" strokeWidth="2" />

        {/* Document Rows */}
        {Object.entries(data).map(([id, snapshotEvents], index) => {
          const y = 90 + index * 70;
          return (
            <g key={id}>
              {/* Document Name Background */}
              <rect
                x={10}
                y={y - 15}
                width={leftMargin - 20}
                height={50}
                fill="#faf9f5"
                stroke="#e9e0d5"
                strokeWidth="1"
                rx="6"
              />

              {/* Document Name */}
              <foreignObject x={15} y={y - 15} width={leftMargin - 30} height={50}>
                <div className="flex h-full items-center justify-start px-2">
                  <Tooltip
                    text={snapshotEvents[0].business_report_outcome_name}
                    className="rounded bg-black text-white"
                    delayDuration={50}
                    side="top"
                  >
                    <div className="font-medium text-primary hover:text-primary/80">
                      <div
                        className="cursor-pointer text-sm font-medium"
                        style={{
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                          minWidth: 0,
                          width: "100%",
                        }}
                        onClick={() => setSelectedSnapshotEventGroupIds(snapshotEvents.map(ev => ev.id))}
                        title={snapshotEvents[0].business_report_outcome_name}
                      >
                        {snapshotEvents[0].business_report_outcome_name}
                      </div>
                    </div>
                  </Tooltip>
                </div>
              </foreignObject>

              {/* Horizontal Line for Document */}
              <line
                x1={leftMargin}
                y1={y + 10}
                x2={leftMargin + timelineWidth}
                y2={y + 10}
                stroke="#dde0e4"
                strokeWidth="1"
              />

              {/*Event Nodes */}

              {snapshotEvents.map(ev => {
                const x = getDatePosition(ev.event_date);
                return (
                  <g key={ev.id} onMouseEnter={e => handleMouseEnter(ev, e)} onMouseLeave={handleMouseLeave}>
                    {/* Event Circle Shadow */}
                    <circle cx={x + 1} cy={y + 11} r="8" fill="rgba(0,0,0,0.08)" />
                    {/* Event Circle */}
                    <circle
                      cx={x}
                      cy={y + 10}
                      r="8"
                      fill={getStatusColor(ev.status, ev.is_overdue)}
                      stroke="white"
                      strokeWidth="2"
                      className="cursor-pointer transition-all duration-200 ease-in-out"
                    />
                    {ev.status === "overdue" && (
                      <circle
                        cx={x}
                        cy={y + 10}
                        r="8"
                        fill="none"
                        stroke={ev.is_overdue ? "#dc2626" : "#3b82f6"} // info
                        strokeWidth="2"
                        opacity="0.6"
                      >
                        <animate attributeName="r" values="8;14;8" dur="2s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite" />
                      </circle>
                    )}
                    {/* Date Label */}
                    <text
                      x={x}
                      y={y + 32}
                      textAnchor="middle"
                      className="fill-current font-sans text-[10px] text-muted-foreground"
                    >
                      {formatMonthYear(ev.event_date)}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
};
